import { Editor } from "@tiptap/core";
import { getLabware } from "@/utils/documentAttributes";
import { EditorState } from "@tiptap/pm/state";
import { Node } from "@tiptap/pm/model";
import { type LabwareObject } from "./labware/labwareManager";
import type { DeckConfiguration } from "@/state/run/useDeckLayoutState";
import { UnitConversionService } from "./utils/unitConversionService";
import type { SystemConfig } from "@/state/configState";
import type {
  ConfigStep,
  ConfigParam,
} from "@/automation/system-config/systemConfigParser"; // Adjust path if needed
import type { StepSettingsData } from "./step-settings/useStepSettings";
import type { StepTipUsage } from "./utils/tipCalculator";

// Re-export LabwareObject so it can be imported from this module
export type { LabwareObject };

export interface ProtocolExportParameterValueLabware {
  type: "labware";
  labware: LabwareObject;
  deckPosition?: number; // Reverted to optional to accommodate base export
  metadata?: Record<string, any>;
}

/** Labware metadata carried in protocol for stateless runner execution. */
export interface ProtocolLabwareConfig {
  typeId: string;
  label: string;
  mdbId: number;
  category: string; // "plate" | "tip" | "adapter" | "device"
  rows: number;
  tipStand?: string;
  adapterType?: string;
  adapterStand?: string;
  specifications: Record<string, string | number | boolean>;
}

export interface ProtocolExport {
  steps: {
    name: string;
    id: string;
    uid?: string; // Unique step instance identifier (from node.attrs.uid)
    configId?: string;
    requiredTipTypeIds?: string[];
    /** Per-step tip usage for display on the tip allocation screen (added by decorateProtocolWithTips). */
    tipUsage?: StepTipUsage;
    parameters: {
      name: string;
      id: string;
      value:
        | string
        | number
        | ProtocolExportParameterValueLabware
        | ProtocolExportParameterValueLabware[];
    }[];
  }[];
  labware: LabwareObject[];
  deckLayout: Record<string, number>;
  heads: {
    id: string;
    label: string;
    specifications: {
      name: string;
      value: string;
      unit?: string;
    }[];
  }[];
  metadata?: {
    generatedAt: string;
    configId: string;
    methodId: string;
    workspaceId?: string;
    generatedBy?: string;
  };
  /** Full labware config for all labware referenced in this protocol (keyed by typeId). */
  labwareConfig?: Record<string, ProtocolLabwareConfig>;
  /** Resolved liquid class defaults for the selected head (keyed by class name, e.g. "aqueous"). */
  liquidClassDefaults?: Record<string, Record<string, number | boolean>>;
  /** Liquid class parameter constraints for the selected head. */
  liquidClassConstraints?: Record<string, { min?: number; max?: number }>;
  /** The selected head ID for this protocol. */
  selectedHead?: string;
}

function getStepName(stepNode: Node): string {
  const stepNameNode = stepNode.firstChild;
  return stepNameNode?.textContent || "";
}

function getParameterValue(
  paramNode: Node,
  labwareMap: Map<string, LabwareObject>,
  deckLayout: Record<string, number>,
  configParamDefinition?: ConfigParam | null,
  unitConversionService?: UnitConversionService
): string | number | ProtocolExportParameterValueLabware {
  // Allow number return type
  const paramValueNode = paramNode.lastChild;
  if (!paramValueNode) return "";

  // Check if this is a labware select value
  const selectValue = paramValueNode.firstChild;
  if (selectValue?.type.name === "selectValue") {
    const selectedId = selectValue.attrs.selectedId;
    const metadata = selectValue.attrs.metadata;
    const labware = labwareMap.get(selectedId);
    const deckPositionNumber = deckLayout[selectedId]; // Position is now a number again

    if (labware && deckPositionNumber !== undefined) {
      return {
        type: "labware",
        labware,
        deckPosition: deckPositionNumber, // Use the number directly
        metadata,
      };
    }
    // If labware selection is somehow invalid, fallback to text or handle error
    return paramValueNode.textContent || "";
  }

  // Handle plain text value, potentially with unit conversion
  const textValue = paramValueNode.textContent || "";

  // Perform unit conversion if applicable
  if (
    unitConversionService &&
    configParamDefinition?.unit && // Check if a default unit is defined
    configParamDefinition?.type && // Check if type is defined
    [
      "volume",
      "duration",
      "temperature",
      "range",
      "speed",
      "rotation",
      "float",
      "number",
    ].includes(configParamDefinition.type) // Check if type requires conversion
  ) {
    try {
      const { value: originalValue, unit: originalUnit } =
        unitConversionService.parseValueWithUnit(textValue);

      if (isNaN(originalValue)) {
        console.warn(
          `Could not parse numeric value from "${textValue}" for parameter "${configParamDefinition.name}". Returning original text.`
        );
        return textValue; // Return original text if parsing fails
      }

      // Convert to the default unit specified in the config
      const convertedValue = unitConversionService.convert(
        originalValue,
        originalUnit || configParamDefinition.unit, // Use original unit if present, else assume default
        configParamDefinition.unit // Target unit is the default from config
      );

      // Return the numeric value after conversion
      // Optionally round or format the number here if needed
      return convertedValue;
    } catch (error) {
      console.warn(
        `Unit conversion failed for parameter "${configParamDefinition.name}" with value "${textValue}" and target unit "${configParamDefinition.unit}". Returning original text. Error:`,
        error
      );
      return textValue; // Fallback to original text on conversion error
    }
  }

  // Return plain text value if no conversion needed/possible
  return textValue;
}

/**
 * Converts step settings to protocol export parameters.
 * Maps liquid handling settings to appropriate parameter names and handles overrides.
 */
function convertStepSettingsToParameters(
  stepSettings: StepSettingsData,
  existingParameterNames: string[]
): {
  name: string;
  id: string;
  value: string | number;
}[] {
  const parameters: {
    name: string;
    id: string;
    value: string | number;
  }[] = [];

  // Handle aspirate settings with defaults
  if (stepSettings.aspirate) {
    parameters.push({
      name: "aspirate_speed",
      id: "aspirate_speed",
      value: stepSettings.aspirate.speed ?? 200,
    });
    parameters.push({
      name: "aspirate_z_offset",
      id: "aspirate_z_offset",
      value: stepSettings.aspirate.zOffset ?? 2.0,
    });
    parameters.push({
      name: "z_drive_speed",
      id: "z_drive_speed",
      value: stepSettings.aspirate.zDriveSpeed ?? 0,
    });
    parameters.push({
      name: "premix_count",
      id: "premix_count",
      value: stepSettings.aspirate.premixCount ?? 0,
    });
    parameters.push({
      name: "premix_speed",
      id: "premix_speed",
      value: stepSettings.aspirate.premixSpeed ?? 0,
    });
    parameters.push({
      name: "wait_time",
      id: "wait_time",
      value: stepSettings.aspirate.waitTime ?? 1.0,
    });
  }

  // Handle dispense settings with defaults
  if (stepSettings.dispense) {
    parameters.push({
      name: "dispense_speed",
      id: "dispense_speed",
      value: stepSettings.dispense.speed ?? 100,
    });
    parameters.push({
      name: "dest_z_offset",
      id: "dest_z_offset",
      value: stepSettings.dispense.zOffset ?? 1.0,
    });
    parameters.push({
      name: "blowout_volume",
      id: "blowout_volume",
      value: stepSettings.dispense.blowoutVolume ?? 100.0,
    });
    parameters.push({
      name: "touch_off",
      id: "touch_off",
      value: stepSettings.dispense.touchOff === true ? "yes" : "no",
    });
    parameters.push({
      name: "dispense_from_bottom",
      id: "dispense_from_bottom",
      value: stepSettings.dispense.fromBottom === true ? "yes" : "no",
    });
    parameters.push({
      name: "wait_after_dispense",
      id: "wait_after_dispense",
      value: stepSettings.dispense.waitAfterDispense ?? 0.0,
    });
  }

  // Handle liquid class selection
  if (stepSettings.liquidClass) {
    parameters.push({
      name: "liquid_class",
      id: "liquid_class",
      value: stepSettings.liquidClass,
    });
  }

  return parameters;
}

/**
 * Finds the labware ID selected for a given role ("source" / "destination" / …)
 * within a specific step instance, by matching the step's parameter instances
 * against the config params carrying that role and reading the selected id.
 */
export function findLabwareIdForStepRole(
  stepNode: Node,
  configStepDefinition: ConfigStep | null | undefined,
  role: string
): string | undefined {
  if (!configStepDefinition) return undefined;

  const roleParamDefinitions = configStepDefinition.params.filter(
    (p) => p.role === role
  );
  if (!roleParamDefinitions.length) return undefined; // No such role for this step type

  let labwareId: string | undefined;

  // Find the 'parameters' child node within the stepNode
  let paramsNode: Node | undefined;
  stepNode.forEach((node: Node) => {
    if (node.type.name === "parameters") {
      paramsNode = node;
    }
  });
  if (!paramsNode) return undefined; // No parameters node found in this step instance

  // Iterate through the actual parameter nodes within this step instance
  paramsNode.content.forEach((paramInstanceNode: Node) => {
    if (labwareId) return; // Stop if already found

    if (paramInstanceNode.type.name === "parameter") {
      const paramNameNode = paramInstanceNode.firstChild;
      const paramName = paramNameNode?.textContent || "";

      // Check if this instance parameter matches one of the defined role params
      if (
        roleParamDefinitions.some(
          (def) => def.name.toLowerCase() === paramName.toLowerCase()
        )
      ) {
        const paramValueNode = paramInstanceNode.lastChild;
        const selectValue = paramValueNode?.firstChild;
        if (
          selectValue?.type.name === "selectValue" &&
          selectValue.attrs.selectedId
        ) {
          labwareId = selectValue.attrs.selectedId;
        }
      }
    }
  });

  return labwareId;
}

/**
 * Helper function to find the source labware ID selected within a specific step instance
 */
function findSourceLabwareIdForStep(
  stepNode: Node,
  configStepDefinition?: ConfigStep | null
): string | undefined {
  return findLabwareIdForStepRole(stepNode, configStepDefinition, "source");
}

/**
 * Get parameters for a specific step node.
 */
function getParameters(
  stepNode: Node,
  labwareMap: Map<string, LabwareObject>,
  deckLayout: Record<string, number>,
  configStepDefinition: ConfigStep, // Made non-nullable based on usage
  unitConversionService: UnitConversionService,
  sourceToTipMap: Map<string, string>
): {
  name: string;
  id: string;
  value: string | number | ProtocolExportParameterValueLabware;
}[] {
  const parameters: {
    name: string;
    id: string;
    value: string | number | ProtocolExportParameterValueLabware;
  }[] = [];

  let paramsNode: Node | undefined;
  stepNode.forEach((node: Node) => {
    if (node.type.name === "parameters") {
      paramsNode = node;
    }
  });

  // Extract step settings from step node attributes
  const stepSettings = stepNode.attrs.stepSettings as
    | StepSettingsData
    | undefined;

  // Get existing parameter names to check for conflicts
  const existingParameterNames: string[] = [];
  if (paramsNode) {
    paramsNode.content.forEach((paramInstanceNode: Node) => {
      if (paramInstanceNode.type.name === "parameter") {
        const paramNameNode = paramInstanceNode.firstChild;
        const paramName = paramNameNode?.textContent || "";
        existingParameterNames.push(paramName);
      }
    });
  }

  // Add liquid handling parameters from step settings if they exist
  if (stepSettings) {
    try {
      const liquidHandlingParameters = convertStepSettingsToParameters(
        stepSettings,
        existingParameterNames
      );

      // Ensure any existing liquid_class is overridden by stepSettings value in base export as well.
      const hasLiquidClassFromSettings = liquidHandlingParameters.some(
        (p) => p.id === "liquid_class"
      );
      if (hasLiquidClassFromSettings) {
        const idx = existingParameterNames.findIndex(
          (n) => n.toLowerCase() === "liquid_class"
        );
        if (idx >= 0) existingParameterNames.splice(idx, 1);
      }

      parameters.push(...liquidHandlingParameters);
    } catch (error) {
      console.warn(
        `Failed to convert step settings to parameters for step "${configStepDefinition.name}":`,
        error
      );
    }
  }

  // Add existing parameters from parameter nodes
  if (paramsNode) {
    paramsNode.content.forEach((paramInstanceNode: Node) => {
      if (paramInstanceNode.type.name === "parameter") {
        const paramNameNode = paramInstanceNode.firstChild;
        const paramName = paramNameNode?.textContent || "";

        // If this is the liquid_class parameter and we already have a liquid_class
        // from stepSettings, skip the existing one to avoid empty/placeholder values overriding
        if (
          paramName.toLowerCase() === "liquid_class" &&
          parameters.some((p) => p.id === "liquid_class")
        ) {
          return; // skip, we keep the stepSettings-derived liquid_class
        }

        const configParamId = paramInstanceNode.attrs.configParamId as
          | string
          | null;
        const configParamDefinition =
          (configParamId
            ? configStepDefinition.params.find((p) => p.id === configParamId)
            : undefined) ??
          configStepDefinition.params.find(
            (p) => p.name.toLowerCase() === paramName.toLowerCase()
          );

        const value = getParameterValue(
          paramInstanceNode,
          labwareMap,
          deckLayout,
          configParamDefinition,
          unitConversionService
        );

        parameters.push({
          name: paramName,
          id: configParamDefinition?.id || paramName,
          value,
        });
      }
    });
  }

  // Decorate with tip parameter if needed
  const tipsDef = configStepDefinition.params.find((p) => p.role === "tips");
  if (tipsDef) {
    const sourceId = findSourceLabwareIdForStep(stepNode, configStepDefinition);
    if (sourceId) {
      // Get the required tip type for this step from the config
      const requiredTipTypeId =
        tipsDef.options && tipsDef.options.length > 0
          ? tipsDef.options[0]
          : null;

      if (requiredTipTypeId) {
        // Look up the tip using both source ID and tip type
        const compositeKey = `${sourceId}:${requiredTipTypeId}`;
        const tipId = sourceToTipMap.get(compositeKey);
        const tipLabware = tipId ? labwareMap.get(tipId) : undefined;

        if (tipLabware) {
          const tipPosition = deckLayout[tipLabware.id]; // Use Record<string, number> map
          parameters.push({
            name: tipsDef.name,
            id: tipsDef.id,
            value: {
              type: "labware",
              labware: tipLabware,
              deckPosition: tipPosition, // Already a number or undefined
              metadata: {}, // Add empty metadata or relevant data if available
            },
          });
        } else {
          // console.warn(`Tip labware not found for source ${sourceId} in step ${configStepDefinition.name}`);
        }
      }
    }
  }

  return parameters;
}

/**
 * Creates a deck layout mapping from the deck configuration
 * @param deckConfiguration - The current deck configuration
 * @returns A mapping of labware IDs to numeric deck positions
 */
export function createDeckLayoutFromConfig(
  deckConfiguration: DeckConfiguration | null | undefined
): Record<string, number> {
  const deckLayout: Record<string, number> = {};

  // If no deck configuration, return empty layout
  if (!deckConfiguration) {
    return {};
  }

  // Map positions to numeric indices for the protocol
  const positions = deckConfiguration.positions;
  positions.forEach((position: any, index: number) => {
    if (position.currentLabware?.id) {
      // Add 1 to index because robot positions are typically 1-indexed
      deckLayout[position.currentLabware.id] = index + 1;
    }
  });

  return deckLayout;
}

/**
 * Deprecated: Use `exportProtocolBase` followed by decorators and then add DeckConfiguration.
 * Generates a complete protocol export including assigned deck layout and tip mapping.
 *
 * @param editor The Tiptap editor instance.
 * @param activeConfig The active system configuration.
 * @param deckConfiguration The final, assigned deck configuration.
 * @param sourceToTipMap A map linking source labware IDs to their assigned tip labware IDs.
 * @returns The complete ProtocolExport object.
 */
export function exportProtocol(
  editor: Editor,
  activeConfig: SystemConfig,
  deckConfiguration: DeckConfiguration, // Input is still full DeckConfiguration
  sourceToTipMap: Map<string, string>,
  selectedHeadId?: string | null
): ProtocolExport {
  const { doc } = editor.state;
  const labwareMap = new Map<string, LabwareObject>();
  const uniqueLabware: LabwareObject[] = [];
  const unitConversionService = new UnitConversionService(); // Initialize service

  // Find all unique labware used in the document
  doc.descendants((node) => {
    if (node.type.name === "selectValue" && node.attrs.selectedId) {
      const lw = node.attrs.metadata as LabwareObject; // Assuming metadata is LabwareObject
      if (lw && !labwareMap.has(node.attrs.selectedId)) {
        labwareMap.set(node.attrs.selectedId, lw);
        uniqueLabware.push(lw);
      }
    }
    return true;
  });

  // Find the adapter labware if present in the deck configuration
  const adapterLabware = deckConfiguration.positions
    .map((p) => p.currentLabware)
    .find((lw) => lw?.typeId === "tipbox_adapter");

  // Create the numeric map needed for getParameters
  const numericDeckLayoutMap = createDeckLayoutFromConfig(deckConfiguration);

  const exportedSteps = [] as ProtocolExport["steps"];
  doc.content.forEach((stepNode) => {
    if (stepNode.type.name === "step") {
      const stepName = getStepName(stepNode);
      const configStepId = stepNode.attrs.configStepId as string | null;
      const configStepDefinition =
        (configStepId
          ? activeConfig.steps.find((s) => s.id === configStepId)
          : undefined) ?? activeConfig.steps.find((s) => s.name === stepName);

      if (configStepDefinition) {
        const parameters = getParameters(
          stepNode,
          labwareMap,
          numericDeckLayoutMap,
          configStepDefinition,
          unitConversionService,
          sourceToTipMap
        );
        exportedSteps.push({
          name: stepName,
          id: configStepDefinition.id,
          parameters,
        });
      } else {
        console.warn(
          `Definition not found for step: ${stepName} (config id: ${configStepId ?? "unbound"})`
        );
      }
    }
  });

  // Combine explicitly defined labware with potentially added tip/adapter labware
  const allLabwareIds = new Set(uniqueLabware.map((lw) => lw.id));
  if (adapterLabware && !allLabwareIds.has(adapterLabware.id)) {
    uniqueLabware.push(adapterLabware);
    allLabwareIds.add(adapterLabware.id);
  }

  // Extract the tip IDs from the sourceToTipMap, handling the new composite key format
  sourceToTipMap.forEach((tipId, key) => {
    // The key now is in format "sourceId:tipTypeId", but we only need the tipId value
    const tipLabware = labwareMap.get(tipId);
    if (tipLabware && !allLabwareIds.has(tipLabware.id)) {
      uniqueLabware.push(tipLabware);
      allLabwareIds.add(tipLabware.id);
    }
  });

  const allHeads = activeConfig.heads || [];
  const exportHeads = selectedHeadId
    ? allHeads.filter((h) => h.id === selectedHeadId)
    : allHeads;

  return {
    steps: exportedSteps,
    labware: uniqueLabware,
    deckLayout: numericDeckLayoutMap,
    heads: exportHeads,
  };
}

/**
 * Gets the raw value of a parameter node, performing unit conversion if applicable,
 * and resolving labware selections (without deckPosition).
 */
function getParameterValueBase(
  paramNode: Node,
  configParamDefinition: ConfigParam | null,
  unitConversionService: UnitConversionService,
  labwareMap: Map<string, LabwareObject>
): string | number | ProtocolExportParameterValueLabware {
  const paramValueNode = paramNode.lastChild;
  if (!paramValueNode) return "";

  // Check for labware select
  const selectValue = paramValueNode.firstChild;
  if (selectValue?.type.name === "selectValue") {
    const selectedId = selectValue.attrs.selectedId;
    const metadata = selectValue.attrs.metadata;
    const labware = labwareMap.get(selectedId);
    if (labware) {
      return { type: "labware", labware, metadata };
    }
    // Fallback to raw text
    return paramValueNode.textContent || "";
  }

  // Handle plain text value, potentially with unit conversion
  const textValue = paramValueNode.textContent || "";

  // Perform unit conversion if applicable
  if (
    unitConversionService &&
    configParamDefinition?.unit && // Check if a default unit is defined
    configParamDefinition?.type && // Check if type is defined
    [
      "volume",
      "duration",
      "temperature",
      "range",
      "speed",
      "rotation",
      "float",
      "number",
    ].includes(configParamDefinition.type) // Check if type requires conversion
  ) {
    try {
      const { value: originalValue, unit: originalUnit } =
        unitConversionService.parseValueWithUnit(textValue);

      if (isNaN(originalValue)) {
        console.warn(
          `Could not parse numeric value from "${textValue}" for parameter "${configParamDefinition.name}" in base export. Returning original text.`
        );
        return textValue; // Return original text if parsing fails
      }

      // Convert to the default unit specified in the config
      const convertedValue = unitConversionService.convert(
        originalValue,
        originalUnit || configParamDefinition.unit, // Use original unit if present, else assume default
        configParamDefinition.unit // Target unit is the default from config
      );

      return convertedValue;
    } catch (error) {
      console.warn(
        `Unit conversion failed for parameter "${configParamDefinition.name}" with value "${textValue}" and target unit "${configParamDefinition.unit}" in base export. Returning original text. Error:`,
        error
      );
      return textValue; // Fallback to original text on conversion error
    }
  }

  if (
    configParamDefinition?.type &&
    ["range", "float", "number"].includes(configParamDefinition.type)
  ) {
    return parseFloat(textValue);
  }

  // Return plain text value if no conversion needed/possible
  return textValue;
}

/**
 * Extracts parameters for a step based on its config definition, resolving labware selections,
 * and ignoring roles like 'tips' and 'adapter'.
 */
function getParametersBase(
  stepNode: Node,
  configStepDefinition: ConfigStep,
  unitConversionService: UnitConversionService,
  labwareMap: Map<string, LabwareObject>
): {
  name: string;
  id: string;
  value: string | number | ProtocolExportParameterValueLabware;
}[] {
  const parameters: {
    name: string;
    id: string;
    value: string | number | ProtocolExportParameterValueLabware;
  }[] = [];
  let paramsNode: Node | undefined;

  stepNode.forEach((node: Node) => {
    if (node.type.name === "parameters") {
      paramsNode = node;
    }
  });

  // Extract step settings from step node attributes
  const stepSettings = stepNode.attrs.stepSettings as
    | StepSettingsData
    | undefined;

  // Get existing parameter names to check for conflicts
  const existingParameterNames: string[] = [];
  if (paramsNode) {
    paramsNode.content.forEach((paramInstanceNode: Node) => {
      if (paramInstanceNode.type.name === "parameter") {
        const paramNameNode = paramInstanceNode.firstChild;
        const paramName = paramNameNode?.textContent || "";
        existingParameterNames.push(paramName);
      }
    });
  }

  // Add liquid handling parameters from step settings if they exist
  if (stepSettings) {
    try {
      const liquidHandlingParameters = convertStepSettingsToParameters(
        stepSettings,
        existingParameterNames
      );

      // Ensure any existing liquid_class is overridden by stepSettings value.
      // If stepSettings produced a liquid_class, drop any pre-existing one later in merge.
      const hasLiquidClassFromSettings = liquidHandlingParameters.some(
        (p) => p.id === "liquid_class"
      );

      if (hasLiquidClassFromSettings) {
        // Pre-emptively remove 'liquid_class' from existingParameterNames so downstream
        // logic never treats it as conflicting.
        const idx = existingParameterNames.findIndex(
          (n) => n.toLowerCase() === "liquid_class"
        );
        if (idx >= 0) existingParameterNames.splice(idx, 1);
      }

      parameters.push(...liquidHandlingParameters);
    } catch (error) {
      console.warn(
        `Failed to convert step settings to parameters for step "${configStepDefinition.name}":`,
        error
      );
    }
  }

  if (!paramsNode) {
    console.warn(
      `Parameters node not found for step "${configStepDefinition.name}". Cannot extract base parameters.`
    );
    return parameters; // Return liquid handling parameters even if no parameters node
  }

  // Iterate through the parameters *defined in the config* for this step type
  configStepDefinition.params.forEach((configParamDefinition: ConfigParam) => {
    const paramName = configParamDefinition.name;
    const paramId = configParamDefinition.id || paramName;

    // Skip parameters that will be layered on later
    if (
      configParamDefinition.role === "tips" ||
      configParamDefinition.role === "adapter"
    ) {
      return; // Skip tips and adapter roles
    }

    // Skip liquid_class parameter if step settings already provide one
    if (paramId.toLowerCase() === "liquid_class") {
      return; // Skip, we keep the stepSettings-derived liquid_class
    }

    // Find the corresponding parameter node *in the actual step instance*.
    // Match id-first so config-side renames don't break the join; fall back to
    // name for unbound nodes.
    let paramInstanceNode: Node | undefined;
    paramsNode?.content.forEach((pNode: Node) => {
      if (pNode.type.name !== "parameter") return;
      const nodeConfigParamId = pNode.attrs.configParamId as string | null;
      if (nodeConfigParamId && nodeConfigParamId === configParamDefinition.id) {
        paramInstanceNode = pNode;
        return;
      }
      if (
        !nodeConfigParamId &&
        pNode.firstChild?.textContent?.toLowerCase() === paramName.toLowerCase()
      ) {
        paramInstanceNode = pNode;
      }
    });

    if (paramInstanceNode) {
      // Parameter instance found, get its value (with potential conversion and labware resolution)
      parameters.push({
        name: paramName,
        id: paramId,
        value: getParameterValueBase(
          paramInstanceNode,
          configParamDefinition,
          unitConversionService,
          labwareMap
        ),
      });
    } else if (!configParamDefinition.hidden && !configParamDefinition.visibleWhen) {
      // Parameter instance *not* found for a *visible* parameter.
      parameters.push({
        name: paramName,
        id: paramId,
        value: "", // Use empty string as placeholder
      });
    }
    // Implicitly skip hidden parameters that have no instance node, and
    // conditional (visibleWhen) parameters that aren't present — a missing
    // conditional param means its condition isn't met, so the block falls back
    // to its runtime default (e.g. Transfer 96 Technique → "Back-and-forth").
  });

  return parameters;
}

/**
 * Exports the base protocol structure from the editor document and system config,
 * resolving labware references directly from the document (no deck positions),
 * and performing unit conversions on parameter values where applicable.
 *
 * @param doc The Tiptap document node.
 * @param activeConfig The active system configuration.
 * @param metadata Optional metadata for protocol generation (methodId required if provided)
 * @returns A ProtocolExport object with steps, labware (resolved from document), and an empty deckLayout.
 */
export function exportProtocolBase(
  state: EditorState,
  activeConfig: SystemConfig,
  metadata?: {
    methodId: string;
    workspaceId?: string;
    userId?: string;
  },
  selectedHeadId?: string | null
): Omit<ProtocolExport, "deckLayout"> & {
  // labware included, deckLayout empty
  labware: LabwareObject[];
  deckLayout: Record<string, never>;
} {
  const unitConversionService = new UnitConversionService();

  // Resolve all labware defined in the document
  const labwareOnDoc = getLabware(state);
  const labwareMap = new Map<string, LabwareObject>(
    labwareOnDoc.map((lw) => [lw.id, lw])
  );

  const steps: ProtocolExport["steps"] = []; // Use ProtocolExport type for steps

  // Traverse document to collect steps
  state.doc.forEach((node) => {
    if (node.type.name !== "step") return;

    const stepName = getStepName(node); // Reuse existing helper
    const configStepId = node.attrs.configStepId as string | null;

    // Find the step definition in the active config — id-first, name fallback
    const configStepDefinition =
      (configStepId
        ? activeConfig.steps.find((s: ConfigStep) => s.id === configStepId)
        : undefined) ??
      activeConfig.steps.find((s: ConfigStep) => s.name === stepName);

    if (!configStepDefinition) {
      console.warn(
        `Could not find config definition for step: ${stepName}. Skipping step.`
      );
      return; // Skip this step if config not found
    }

    // Pass the service and labware map down
    const parameters = getParametersBase(
      node,
      configStepDefinition,
      unitConversionService,
      labwareMap
    );

    steps.push({
      name: stepName,
      id: configStepDefinition.id, // Config step definition ID (e.g., "transfer", "centrifuge")
      uid: node.attrs.uid, // Unique step instance identifier for tip allocation
      configId: configStepDefinition.id,
      parameters, // Parameters now include labware structures
    });
  });

  // Build metadata if provided
  const protocolMetadata = metadata
    ? {
        generatedAt: new Date().toISOString(),
        configId: activeConfig.name,
        methodId: metadata.methodId,
        workspaceId: metadata.workspaceId,
        generatedBy: metadata.userId,
      }
    : undefined;

  // Return the basic structure with resolved labware, empty deck layout, and heads from config
  const allHeads = activeConfig.heads || [];
  const exportHeads = selectedHeadId
    ? allHeads.filter((h) => h.id === selectedHeadId)
    : allHeads;

  return {
    steps,
    labware: labwareOnDoc,
    deckLayout: {},
    heads: exportHeads,
    ...(protocolMetadata && { metadata: protocolMetadata }),
  };
}

// Helper function to download the protocol as JSON
export function downloadProtocolAsJson(
  protocol: ProtocolExport,
  filename = "protocol.json"
) {
  // Create a blob from the protocol object
  const blob = new Blob([JSON.stringify(protocol, null, 2)], {
    type: "application/json",
  });
  // Create a URL for the blob
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
